import Button from '@/components/atoms/Button';
import Input from '@/components/atoms/Input';
import Modal from '@/components/atoms/modal';
import DeleteModal from '@/components/atoms/modal/DeleteModal';
import Spinner from '@/components/atoms/Spinner';
import Text from '@/components/atoms/Text';
import { notify } from '@/helpers/index';
import { useExternalLink } from '@/hooks/useExternalLink';
import { externalLinkStore } from '@/state/ExternalLinkStore';
import { ExternalLink } from '@/types/external_link.type';
import { Icon } from '@iconify/react';
import { Formik } from 'formik';
import { observer } from 'mobx-react-lite';
import { FC, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as yup from 'yup';

const externalLinkScheme = yup.object().shape({
  external_link: yup
    .string()
    .matches(
      /^(https?:\/\/|www\.)[\w.-]+(\.[a-z]{2,})+(\/[\w.-]*)*\/?$/,
      'Please enter a valid URL.',
    )
    .required('External link is required'),
});

type ExternalLinkValues = yup.InferType<typeof externalLinkScheme>;

const Profile: FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialData, setInitialData] = useState<ExternalLink | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const externalLinks = externalLinkStore.externalLinks;
  const { createExternalLink, deleteExternalLink, updateExternalLink } =
    useExternalLink();
  const { id: userId } = useParams<{ id: string }>();

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleSubmit = (values: ExternalLinkValues) => {
    if (!userId) {
      return notify('error', 'User ID is required');
    }

    const links = [...externalLinks, { link: values?.external_link }];

    if (!initialData) {
      if (links.length > 5) {
        return notify('error', 'You can only add 5 external links');
      }
      createExternalLink({
        userId,
        link: values?.external_link,
      });
    } else {
      updateExternalLink(initialData._id, { link: values?.external_link });
    }
    closeModal();
  };

  return (
    <div className='flex flex-col gap-y-1'>
      <Text size='md' color='text-black'>
        External profiles
      </Text>
      {externalLinkStore.loading ? (
        <div className='py-4'>
          <Spinner size={20} />
        </div>
      ) : externalLinks?.length === 0 ? (
        <Text size='sm' color='text-[#656971]'>
          No external profiles
        </Text>
      ) : (
        externalLinks?.map(externalLink => (
          <div
            key={externalLink?._id}
            className={`rounded-[4px] my-1 flex items-center justify-between group`}
          >
            <Link
              to={externalLink?.link}
              target='_blank'
              rel='noopener noreferrer'
              className='w-[80%] hover:underline'
            >
              <Text size='sm' className='whitespace-no-wrap truncate '>
                {externalLink?.link}
              </Text>
            </Link>
            {/* Actions */}
            <div className='flex gap-1'>
              <button
                className='p-1 hover:bg-secondary/10 rounded'
                onClick={() => {
                  setInitialData(externalLink);
                  setIsModalOpen(true);
                }}
              >
                <svg
                  className={`w-6 h-6 text-secondary cursor-pointer invisible group-hover:visible`}
                  fill='currentColor'
                  viewBox='0 0 20 20'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    fillRule='evenodd'
                    d='M13.293 5.293a1 1 0 00-1.414 0L4 12.586V16h3.414l7.293-7.293a1 1 0 000-1.414l-2-2zM5.414 14H5v-.414l7-7L12.586 7l-7 7z'
                    clipRule='evenodd'
                  />
                </svg>
              </button>
              <button
                className='p-1 hover:bg-red-500/10 rounded'
                onClick={() => {
                  setInitialData(externalLink);
                  setIsDeleting(true);
                }}
              >
                <svg
                  className={`w-6 h-6 text-red-500 cursor-pointer invisible group-hover:visible`}
                  fill='currentColor'
                  viewBox='0 0 20 20'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                    clipRule='evenodd'
                  />
                </svg>
              </button>
            </div>
          </div>
        ))
      )}
      <Button
        LeftIcon={<Icon icon='heroicons:plus' strokeWidth={10} />}
        text='Add External Profile'
        variant='outline'
        className='w-[180px] h-[32px] mt-2 whitespace-nowrap'
        onClick={() => {
          setInitialData(null);
          openModal();
        }}
      />
      <Modal
        show={isModalOpen}
        openModal={openModal}
        closeModal={closeModal}
        title={(initialData ? 'Update' : 'Add') + ' External Profile'}
        className='z-[10000] inline-block py-6 my-8 w-[100%] max-w-[520px] overflow-hidden text-left align-top  transition-all transform bg-white shadow-xl rounded-[7px]'
      >
        <div className='px-4'>
          <Formik
            onSubmit={handleSubmit}
            validationSchema={externalLinkScheme}
            initialValues={{
              external_link: initialData?.link ?? '',
            }}
          >
            {({
              values,
              errors,
              isSubmitting,
              isValid,
              dirty,
              touched,
              handleChange,
              handleSubmit,
            }) => (
              <form onSubmit={handleSubmit}>
                <Input
                  placeholder='Enter URL'
                  label='URL'
                  type='text'
                  name='external_link'
                  onChange={handleChange('external_link')}
                  value={values?.external_link}
                  error={
                    touched?.external_link && errors?.external_link
                      ? errors?.external_link
                      : ''
                  }
                />

                <div className='mt-4 text-center flex justify-between space-x-4'>
                  <div className='flex flex-1'>
                    <Button
                      size='sm'
                      text='Cancel'
                      onClick={closeModal}
                      variant='outline'
                      className='w-full'
                      type='button'
                    />
                  </div>

                  <div className='flex flex-1'>
                    <Button
                      size='sm'
                      text={initialData ? 'Update' : 'Create'}
                      type='submit'
                      className='hover:bg-primary-medium w-full'
                      loading={isSubmitting}
                      disabled={
                        initialData
                          ? isSubmitting || !isValid
                          : !isValid || !dirty || isSubmitting
                      }
                    />
                  </div>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </Modal>

      <DeleteModal
        show={isDeleting}
        content={
          'Are you sure you want to delete ' + initialData?.link + ' link?'
        }
        onDelete={() => {
          setIsDeleting(false);
          deleteExternalLink(initialData?._id as string);
        }}
        setShow={setIsDeleting}
        title='Delete External Profile Link'
        deleteTextButton='Delete'
      />
    </div>
  );
};

export default observer(Profile);
